import * as types from '../actionTypes';
// ---- parent imports ----

import INITIAL_STATE from './initialState';
import {
	getStateAfterSubmission,
	getStateAfterIncomeStatementUpdate,
	getStateAfterBalanceSheetUpdate,
	getStateAfterFinancialRatiosUpdate,
	getStateAfterAllSystemStatementsUpdated,
	getStateAfterSystemBalanceSheetUpdate,
	getStateAfterFinancialRatiosSubmission,
} from './helpers';
// ---- local imports ----


function reduce(state = INITIAL_STATE, action = {}) {
	switch (action.type) {
	case types.FINANCIAL_STATEMENTS_LOAD:
	case types.FINANCIAL_STATEMENTS_NEXT_TURN_LOAD:
		return state.merge({
			...action.financialStatements,
			isLoaded: true
		});
	case types.FINANCIAL_STATEMENTS_LOST_INVENTORY_UPDATED:
		return state.merge({
			lostInventoryByQuarterNum: action.lostInventoryByQuarterNum
		});
	case types.FINANCIAL_STATEMENTS_UPDATE_CAN_SUBMIT_FINANCIAL_STATEMENTS:
		return state.merge({
			canSubmitFinancialStatements: action.canSubmitFinancialStatements
		});
	case types.FINANCIAL_STATEMENTS_SET_HAS_ATTEMPTED_TO_SUBMIT_STATEMENTS:
		return state.merge({
			hasAttemptedToSubmitFinancialStatements: action.hasAttemptedToSubmitFinancialStatements
		})
	case types.FINANCIAL_STATEMENTS_SET_HAS_ATTEMPTED_TO_SUBMIT_RATIOS:
		return state.merge({
			hasAttemptedToSubmitFinancialRatios: action.hasAttemptedToSubmitFinancialRatios
		});
	case types.FINANCIAL_STATEMENTS_SET_SUBMISSION_ERRORS_CONFIGURATION:
		const hasAttemptedToSubmitFinancialStatements = 
			action.hasAttemptedToSubmitFinancialStatements ?? state.hasAttemptedToSubmitFinancialStatements;
		return state.merge({
			hasAttemptedToSubmitFinancialStatements,
			errorConfiguration: action.errorConfiguration,
			displayedStatement: action.displayedStatement,
		}, { deep: true } );
	case types.FINANCIAL_STATEMENTS_SET_ERROR_MESSAGES:
		return state.merge({
			errorMessageForStatements: action.errorMessageForStatements,
		}, { deep: true });
	case types.FINANCIAL_STATEMENTS_RESET_ERROR_HANDLING:
		return state.merge({
			displayedStatement: 'incomeStatement',
			hasAttemptedToSubmitFinancialStatements: false,
		}, { deep: true });
	case types.FINANCIAL_STATEMENTS_UPDATE_RATIOS_ERROR_FIELD:
		return state.merge({
			financialRatiosErrorField: action.errorField,
			hasAttemptedToSubmitFinancialRatios: true,
		});
	case types.FINANCIAL_STATEMENTS_SET_DISPLAYED_STATEMENT:
		return state.merge({
			displayedStatement: action.displayedStatement,
		});
	case types.FINANCIAL_STATEMENTS_SET_FINANCIAL_RATIOS_CARD_OPEN:
		return state.merge({
			isFinancialRatiosCardOpen: action.isOpen,
		});
	case types.FINANCIAL_STATEMENTS_UPDATE_SUBMISSION_STATUS_FOR_QUARTER:
		return getStateAfterSubmission(state, action);
	case types.FINANCIAL_STATEMENTS_UPDATE_RATIOS_SUBMISSION_STATUS_FOR_QUARTER:
		return getStateAfterFinancialRatiosSubmission(state, action);
	case types.FINANCIAL_STATEMENTS_UPDATE_INCOME_STATEMENT_FOR_QUARTER:
		return getStateAfterIncomeStatementUpdate(state, action);
	case types.FINANCIAL_STATEMENTS_UPDATE_BALANCE_SHEET_FOR_QUARTER:
		return getStateAfterBalanceSheetUpdate(state, action);
	case types.FINANCIAL_STATEMENTS_UPDATE_FINANCIAL_RATIOS_FOR_QUARTER:
		return getStateAfterFinancialRatiosUpdate(state, action);

	case types.FINANCIAL_STATEMENTS_SYSTEM_UPDATE_ALL_FOR_QUARTER:
		return getStateAfterAllSystemStatementsUpdated(state, action);
	case types.FINANCIAL_STATEMENTS_SYSTEM_UPDATE_BALANCE_SHEET_FOR_QUARTER:
		return getStateAfterSystemBalanceSheetUpdate(state, action);

	case types.FINANCIAL_STATEMENTS_SAVE_OPENING_INVENTORY_FOR_QUARTER:
		return getUpdatedStateAfterOpeningInventoryUpdate(state, action);
	case types.FINANCIAL_STATEMENTS_SAVE_CONTRIBUTED_CAPITAL:
		return getUpdatedStateAfterContributedCapitalUpdate(state, action);
	case types.FINANCIAL_STATEMENTS_UPDATE_ACCOUNT_RECEIVABLES:
		return getStateUpdateAfterAccountReceivablesUpdate(state, action);
	case types.FINANCIAL_STATEMENTS_DISTRIBUTE_PROFIT:
		return getStateUpdateAfterProfitDistribtion(state, action);
	case types.FINANCIAL_STATEMENTS_SET_OPENING_RETAINED_EARNINGS_FOR_MONTH:
		return getStateAfterSettingOpeningRetainedEarningsForMonth(state, action);
	case types.FINANCIAL_STATEMENTS_UPDATE_CURRENT_RETAINED_EARNINGS:
		return getStateAfterUpdatingCurrentRetainedEarnings(state, action);
	default:
		return state;
	}
}

function getStateUpdateAfterAccountReceivablesUpdate(state, action) {
	const { amount, receivingMonth, receivingYear } = action;
	const currYearAccountReceivables = state.accountReceivables[receivingYear] || {};
	const accountReceivablesForMonth = currYearAccountReceivables[receivingMonth] ?? 0;

	const updatedState = state.merge(
		{
			accountReceivables: {
				...state.accountReceivables,
				[receivingYear]: {
					...currYearAccountReceivables,
					[receivingMonth]: accountReceivablesForMonth + amount
				}
			},
		}, { deep: true });

	return updatedState;
}

function getUpdatedStateAfterContributedCapitalUpdate(state, action) {
	const updatedState = state.merge({
		contributedCapital: action.contributedCapital
	});

	return updatedState;
}

function getUpdatedStateAfterOpeningInventoryUpdate(state, action) {
	const updatedState = state.merge({
		openingInventoryByQuarterNum: {
			...state.openingInventoryByQuarterNum,
			[action.quarterNum]: action.openingInventory
		}
	});

	return updatedState;
}

function getStateUpdateAfterProfitDistribtion(state, action) {
	const { amount, month, year } = action;
	const currYearDistributedProfits = state.distributedProfits[year] || {};
	const updatedState = state.merge(
		{	
			currentRetainedEarnings: state.currentRetainedEarnings - amount,
			distributedProfits: {
				...state.distributedProfits,
				[year]: {
					...currYearDistributedProfits,
					[month]: amount
				}
			},
		}, { deep: true });

	return updatedState;
}

function getStateAfterSettingOpeningRetainedEarningsForMonth(state, action) {
	const { month, year } = action;
	const currYearOpeningRetainedEarningsByMonth = state.openingRetainedEarningsByMonth[year] || {};

	const updatedState = state.merge(
		{				
			openingRetainedEarningsByMonth: {
				...state.openingRetainedEarningsByMonth,
				[year]: {
					...currYearOpeningRetainedEarningsByMonth,
					[month]: state.currentRetainedEarnings
				},
			},
		}, { deep: true });

	return updatedState;
}

function getStateAfterUpdatingCurrentRetainedEarnings(state, action) {
	const openingRetainedEarningsByMonth = state.openingRetainedEarningsByMonth;

	const currentYear = Object.keys(openingRetainedEarningsByMonth).at(-1);
	const currYearOpeningRetainedEarningsByMonth = openingRetainedEarningsByMonth[currentYear];
	const latestMonth = Object.keys(currYearOpeningRetainedEarningsByMonth).at(-1);

	const updatedState = state.merge({
		currentRetainedEarnings: action.currentRetainedEarnings,
		openingRetainedEarningsByMonth: {
			...state.openingRetainedEarningsByMonth,
			[currentYear]: {
				...currYearOpeningRetainedEarningsByMonth,
				[latestMonth]: action.currentRetainedEarnings,
			}
		},
	}, { deep: true });

	return updatedState;
}

export default reduce;
